<template>
    <Sidebar
        :id="id"
        v-model:pinned="pinned"
        :title="title"
        :is-shown="isShown"
        :pinnable="pinnable"
        class="sidebar"
        :style="styles"
        @hide="hide"
        @hidden="onHidden"
    >
        <form
            ref="formRef"
            class="form-sidebar__content"
            novalidate
            @submit.prevent
        >
            <StandardField
                v-if="form"
                :field="form"
                :field-id="form.name"
                novalidate
                :label-size="labelSize"
                @section-change="onSectionChange"
                @update-form="update"
            />
        </form>

        <template #footer>
            <div
                class="form-sidebar__footer"
            >
                <Button
                    variant="light"
                    :block="false"
                    @click="hide"
                >
                    Cancel
                </Button>
                <Tooltip
                    v-if="canAct"
                    :title="submitDisabledTooltip"
                    :disabled="!hasNoUsersAvailable"
                >
                    <Button
                        ref="actionButtonRef"
                        :variant="actionButton.variant"
                        :block="false"
                        :cypress-id="actionButton.title"
                        :disabled="!validated || submitBlocked"
                        :visually-disabled="hasNoUsersAvailable"
                        :aria-describedby="hasNoUsersAvailable ? noUsersReasonId : undefined"
                        @click="onSave"
                    >
                        {{ actionButton.title }}
                    </Button>
                </Tooltip>
                <span
                    v-if="hasNoUsersAvailable"
                    :id="noUsersReasonId"
                    class="visually-hidden"
                >
                    {{ submitDisabledTooltip }}
                </span>
            </div>
        </template>
        <div
            class="sidebar__handle"
            role="button"
            tabindex="0"
            aria-label="Resize sidebar"
            @mousedown.stop.prevent="onHandleMovingStart"
        />
    </Sidebar>
</template>

<script lang="ts" setup generic="TSaveResponseType extends t.Type<unknown, unknown, unknown>">
import {
    computed, onMounted, onUnmounted, ref, useId, useTemplateRef,
} from 'vue';
import isType from '../../../scripts/io-ts/isType';
import Sidebar from './Sidebar.vue';
import StandardField from '../Form/StandardField.vue';
import Button from '../Elements/Button.vue';
import Tooltip from '../Elements/Tooltip.vue';
import { Variant } from '../Elements/Button/Variant';
import { validate } from '../../../scripts/helpers/schemaConverter/validators/required';
import type { Form } from '../../../scripts/helpers/schemaConverter/converters/types/Form';
import { FormC, RawFormC } from '../../../scripts/helpers/schemaConverter/converters/types/Form';
import RequestBuilder, {
    ErrorHandler,
    Significance,
} from '../../../scripts/helpers/Request/Builder/RequestBuilder';
import { convertJsonForm, serializeFormFields } from '../../../scripts/mixins/jsonSchemaConverter';
import {
    MessageResponseC,
    ResponseC,
    useSidebarStore,
    type ActionButton,
} from '@/vue/stores/sidebar';
import { makeErrorToast } from '../../composables/toaster';
import type { LabelSize } from '../../../scripts/types/LabelSize';
import { LABEL_SIZE_SM } from '../../../scripts/types/LabelSize';
import { SectionType } from '../../../scripts/types/sections';
import FIELD_RENDER_TYPES from '../../../scripts/helpers/fieldRenderTypes';
import * as t from 'io-ts';
import { isAxiosError } from 'axios';
import type {
    AxiosErrorWithResponse } from '../../../scripts/mixins/defaultAjaxErrorHandler';
import {
    defaultAjaxErrorHandlerFunction,
} from '../../../scripts/mixins/defaultAjaxErrorHandler';
import { updateJsonForm } from './updateJsonForm';

const MIN_SIDEBAR_SIZE = 21;
const MAX_SIDEBAR_SIZE = 42;

type Props = {
    id: string;
    title?: string;
    validatable?: boolean;
    actionButton?: ActionButton;
    action?: string|null;
    saveResponseType: TSaveResponseType | null;
    preAction?: string|null;
    updateAction?: string|null;
    isShown: boolean;
    pinnable?: boolean;
    form?: Form|null;
    labelSize?: LabelSize | null;
}

type Emits = {
    (e: 'update:form', value: Form|null): void,
    (e: 'hide'): void,
    (e: 'save', response: TSaveResponseType): void,
    (e: 'error', payload: { response: unknown, handledRef: { value: boolean } }): void,
    (e: 'hidden'): void,
}

const emit = defineEmits<Emits>();

const props = withDefaults(defineProps<Props>(), {
    title: 'Sidebar',
    validatable: false,
    actionButton: () => ({ title: 'Send', variant: Variant.PRIMARY }),
    action: null,
    preAction: null,
    updateAction: null,
    pinnable: false,
    form: null,
    labelSize: LABEL_SIZE_SM,
});

const sidebarStore = useSidebarStore();

const pinned = computed<boolean>({
    get: () => sidebarStore.isPinned,
    set: (value) => sidebarStore.setIsPinned(value),
});

const sidebarWidth = ref<number>(MIN_SIDEBAR_SIZE);
const sidebarWidthWasSetManually = ref(false);

const handleIsMoving = ref<boolean>(false);
const formRef = ref<HTMLFormElement|null>(null);
const actionButtonRef = useTemplateRef<InstanceType<typeof Button>>('actionButtonRef');

const submitDisabledMetadata = computed(() => {
    return (props.form?.metadata as Record<string, unknown>) || {};
});

const hasNoUsersAvailable = computed(() => {
    return submitDisabledMetadata.value.submitDisabled === true;
});

const submitDisabledTooltip = computed(() => {
    return (submitDisabledMetadata.value.submitDisabledTooltip as string | null) || null;
});

const noUsersReasonId = useId();

const validated = computed<boolean>(() => {
    if (!props.validatable) {
        return true;
    }

    if (props.form === null) {
        return false;
    }

    return validate(props.form);
});

const hasSubmitBlockingField = (form: Form): boolean => Object.values(form.fields)
    .some((field) => (isType(field, FormC)
        ? hasSubmitBlockingField(field)
        : Boolean(field.metadata?.blocksSubmit)));

const submitBlocked = computed<boolean>(
    () => props.form !== null && hasSubmitBlockingField(props.form),
);

const canAct = computed(() => {
    if (!props.form || !props.action) {
        return false;
    }

    return !props.form.disabled;
});

const hide = (): void => {
    emit('hide');
};

const getForm = (): Form => {
    const { form } = props;
    if (!form) {
        throw new Error('Unable to send payload without a form');
    }

    if (form.disabled) {
        throw new Error('You can not send disabled form');
    }

    return form;
};

/**
 * Returns false when the action should be aborted.
 */
const preAct = async (): Promise<boolean> => {
    if (!props.preAction) {
        return true;
    }

    const ignoredFields = [
        FIELD_RENDER_TYPES.MULTI_FILES,
        FIELD_RENDER_TYPES.FILE,
        FIELD_RENDER_TYPES.FILE_UPLOAD,
        FIELD_RENDER_TYPES.AUDIO,
        FIELD_RENDER_TYPES.VIDEO_UPLOAD,
        FIELD_RENDER_TYPES.AVATAR,
    ];

    const form = getForm();
    const payload = serializeFormFields(
        form.fields,
        form.name,
        new FormData(),
        ignoredFields,
    );

    const request = new RequestBuilder(ResponseC)
        .setUrl(props.preAction)
        .setMethod('POST')
        .setFormData(payload)
        .setSignificance(Significance.Main)
        .allow400()
        .build();

    const response = await request.send();
    const responseStatusCode = request.getResponseStatusCode();

    if (responseStatusCode !== 400) {
        return true;
    }

    if (MessageResponseC.is(response)) {
        makeErrorToast(response.message, response.title);

        return false;
    }

    throw new Error('Pre-action failed.');
};

const act = async (): Promise<void> => {
    if (!await preAct()) {
        return;
    }

    if (!props.action) {
        throw new Error('No form URL provided.');
    }

    const form = getForm();
    const payload = serializeFormFields(
        form.fields,
        form.name,
        new FormData(),
    );

    const request = new RequestBuilder(props.saveResponseType || t.unknown)
        .setUrl(props.action)
        .setSignificance(Significance.Main)
        .setErrorHandler(ErrorHandler.None)
        .setFormData(payload)
        .build();

    sidebarStore.setIsSaving(true);
    try {
        const response = await request.send();
        emit('save', response as TSaveResponseType);

        if (!pinned.value) {
            emit('hide');
        }
    } catch (e) {
        if (!isAxiosError(e) || !e.response) {
            defaultAjaxErrorHandlerFunction(e as AxiosErrorWithResponse);
            return;
        }

        const { data } = e.response;

        if (isType(data, RawFormC)) {
            const content = convertJsonForm(data);
            if (content === null) {
                defaultAjaxErrorHandlerFunction(e as AxiosErrorWithResponse);
                return;
            }

            emit('update:form', content);
            return;
        }

        if (MessageResponseC.is(data)) {
            const handledRef = { value: false };
            emit('error', { response: data, handledRef });
            if (!handledRef.value) {
                makeErrorToast(data.message, data.title);
            }
            return;
        }

        defaultAjaxErrorHandlerFunction(e as AxiosErrorWithResponse);
    } finally {
        sidebarStore.setIsSaving(false);
    }
};

const onSectionChange = (section: SectionType) => {
    if (sidebarWidthWasSetManually.value) {
        return;
    }

    if (section === SectionType.EVALUATION) {
        sidebarWidth.value = MAX_SIDEBAR_SIZE;
        return;
    }

    sidebarWidth.value = MIN_SIDEBAR_SIZE;
};

const onHidden = () => {
    emit('hidden');
    sidebarWidthWasSetManually.value = false;
};

const update = async (preserveUpdatedData = true): Promise<void> => {
    if (!props.form) {
        throw new Error('Unable to send payload without a form');
    }

    if (!props.updateAction) {
        throw new Error('This form can not be updated');
    }

    const content = await updateJsonForm(props.form, props.updateAction, preserveUpdatedData);
    emit('update:form', content);
};

const onSave = (): void => {
    const form = formRef.value;

    if (!form) {
        return;
    }

    if (!validated.value || submitBlocked.value) {
        return;
    }

    if (hasNoUsersAvailable.value) {
        return;
    }

    act();
};
type Styles = {
    width: string,
    transitionProperty: string,
};
const styles = computed<Styles|null>(() => {
    if (sidebarWidth.value === MIN_SIDEBAR_SIZE) {
        return null;
    }

    return {
        width: `${sidebarWidth.value}rem`,
        transitionProperty: handleIsMoving.value ? 'none' : 'width',
    };
});
const onHandleMoving = (event: MouseEvent) => {
    if (handleIsMoving.value) {
        const remSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        if (formRef.value?.getBoundingClientRect()) {
            const newSize = (event.clientX - 60) / remSize;
            sidebarWidth.value = newSize > MIN_SIDEBAR_SIZE ? newSize : MIN_SIDEBAR_SIZE;
        }
    }
};

let onHandleMovingEnd: ((e: MouseEvent) => void);

const removeMouseEventListeners = (): void => {
    document.removeEventListener('mousemove', onHandleMoving);
    document.removeEventListener('mouseup', onHandleMovingEnd);
};

onHandleMovingEnd = () => {
    handleIsMoving.value = false;

    removeMouseEventListeners();
};

const onHandleMovingStart = () => {
    sidebarWidthWasSetManually.value = true;
    handleIsMoving.value = true;
    document.addEventListener('mousemove', onHandleMoving);
    document.addEventListener('mouseup', onHandleMovingEnd);
};

const onEnterKeyUp = (event: KeyboardEvent) => {
    if (event.key !== 'Enter') {
        return;
    }

    if (event.target instanceof HTMLTextAreaElement) {
        return;
    }

    const button = actionButtonRef.value?.$el as HTMLButtonElement|undefined;
    if (event.target === button) {
        return;
    }

    if (event.target instanceof Element && event.target.closest('button, a[href], [role="button"]')) {
        return;
    }

    button?.click();
};

onMounted(() => {
    document.addEventListener('keyup', onEnterKeyUp);
});

onUnmounted(() => {
    removeMouseEventListeners();
    document.removeEventListener('keyup', onEnterKeyUp);
});

</script>

<style lang="scss">
@import '../../../styles/abstracts/spacings';
@import '../../../styles/vendors/breakpoints';
@import '../../../styles/abstracts/mixins';

.sidebar {
    position: relative;

    &__handle {
        position: absolute;
        top: 0;
        right: 0;

        z-index: 15;

        width: 0.25rem;
        height: 100%;

        cursor: col-resize;

        transition: background-color 0.3s;

        &:hover,
        &:active {
            background-color: var(--theme-color-surface-accent-primary);
        }
    }
}

.form-sidebar__content {
    height: 100%;
    padding: $spacing-xs $spacing-xl;

    @include media-breakpoint-down(lg) {
        padding: $spacing-xs $spacing-s;
    }

    .scrollable {
        height: calc(100% - 8.5rem);
    }
}

.form-sidebar__footer {
    display: grid;

    grid-auto-columns: 1fr;
    grid-auto-flow: column;
    grid-gap: $spacing-s;

    .tooltip-wrapper {
        display: contents;
    }
}
</style>
